// ═══════════════════════════════════════════════════════════════
// DONOR CAPACITY SERVICE
// Path: src/services/capacityService.js
//
// CRUD for the donor_capacity table (one row per member).
// Treats "no row yet" as a valid state — the Capacity tab still
// renders and shows observed signals; director assessment simply
// contributes 0 to the confidence score until filled in.
// ═══════════════════════════════════════════════════════════════

import { supabase } from "../lib/supabase";
import { debugLog } from "../utils/debugLog";

// ── Field mapping: DB → JS ────────────────────────────────────────
const fromDb = (row) => {
  if (!row) return null;
  return {
    id: row.id,
    memberId: row.member_id,
    capacityBand: row.capacity_band || null,
    inclination: row.inclination || null,
    directorConfidence: row.director_confidence || null,
    estimateSource: row.estimate_source || null,
    narrativeNotes: row.narrative_notes || "",
    updatedBy: row.updated_by || null,
    updatedAt: row.updated_at || null,
    createdAt: row.created_at || null,
  };
};

// ── Field mapping: JS → DB ────────────────────────────────────────
const toDb = (data) => {
  const row = {};
  if (data.capacityBand !== undefined) row.capacity_band = data.capacityBand || null;
  if (data.inclination !== undefined) row.inclination = data.inclination || null;
  if (data.directorConfidence !== undefined)
    row.director_confidence = data.directorConfidence || null;
  if (data.estimateSource !== undefined)
    row.estimate_source = data.estimateSource || null;
  if (data.narrativeNotes !== undefined)
    row.narrative_notes = data.narrativeNotes || "";
  return row;
};

export const capacityService = {
  // Fetch the capacity row for one member. Returns null if absent.
  async getByMember(memberId) {
    if (!memberId) return null;
    try {
      const { data, error } = await supabase
        .from("donor_capacity")
        .select("*")
        .eq("member_id", memberId)
        .maybeSingle();
      if (error) throw error;
      return fromDb(data);
    } catch (err) {
      debugLog.error("capacityService.getByMember", err);
      return null;
    }
  },

  // Upsert the capacity row for one member. Creates if missing.
  async upsert(memberId, patch, userId) {
    if (!memberId) throw new Error("capacityService.upsert: memberId required");
    try {
      const row = {
        member_id: memberId,
        ...toDb(patch),
        updated_by: userId || null,
      };
      const { data, error } = await supabase
        .from("donor_capacity")
        .upsert(row, { onConflict: "member_id" })
        .select("*")
        .single();
      if (error) throw error;
      debugLog.action("capacityService.upsert", { memberId });
      return fromDb(data);
    } catch (err) {
      debugLog.error("capacityService.upsert", err);
      throw err;
    }
  },

  // Fetch all capacity rows, indexed by memberId. Used by the
  // CapacityView portfolio list.
  async getAll() {
    try {
      const { data, error } = await supabase
        .from("donor_capacity")
        .select("*");
      if (error) throw error;
      const byMember = {};
      (data || []).forEach((row) => {
        const mapped = fromDb(row);
        if (mapped) byMember[mapped.memberId] = mapped;
      });
      return byMember;
    } catch (err) {
      debugLog.error("capacityService.getAll", err);
      return {};
    }
  },

  // Fetch capacity rows for many members at once (used by Phase 2
  // portfolio view). Safe no-op if memberIds is empty.
  async getForMembers(memberIds) {
    if (!memberIds || memberIds.length === 0) return {};
    try {
      const { data, error } = await supabase
        .from("donor_capacity")
        .select("*")
        .in("member_id", memberIds);
      if (error) throw error;
      const byMember = {};
      (data || []).forEach((row) => {
        const mapped = fromDb(row);
        if (mapped) byMember[mapped.memberId] = mapped;
      });
      return byMember;
    } catch (err) {
      debugLog.error("capacityService.getForMembers", err);
      return {};
    }
  },
};
